/**
 * Defines versioned contracts for the notification escalation ladder.
 */
import { z } from "zod";
/**
 * E.164: a leading plus, a non-zero country digit and up to fifteen digits total.
 */
export const PhoneNumberSchema = z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/, "Expected an E.164 phone number.");
/**
 * BEFORE_DUE counts business days still available; OVERDUE counts them past the
 * due date. Together they cover the whole ladder without a signed threshold.
 */
export const EscalationTriggerKindSchema = z.enum(["BEFORE_DUE", "OVERDUE"]);
export const EscalationRecipientSchema = z
    .object({
    externalName: z.string().nullable(),
    externalPhone: z.string().nullable(),
    id: z.uuid(),
    userDisplayName: z.string().nullable(),
    userId: z.uuid().nullable(),
})
    .strict();
export const EscalationLevelItemSchema = z
    .object({
    id: z.uuid(),
    isActive: z.boolean(),
    level: z.number().int().min(1).max(20),
    notifyEmail: z.boolean(),
    notifyWhatsApp: z.boolean(),
    recipients: z.array(EscalationRecipientSchema),
    repeatEveryBusinessDays: z.number().int().min(1).nullable(),
    thresholdBusinessDays: z.number().int().min(0),
    title: z.string().min(1).max(120),
    triggerKind: EscalationTriggerKindSchema,
})
    .strict();
export const EscalationLevelListResponseSchema = z
    .object({
    items: z.array(EscalationLevelItemSchema),
})
    .strict();
const escalationRecipientInputSchema = z
    .object({
    externalName: z.string().trim().min(1).max(200).optional(),
    externalPhone: PhoneNumberSchema.optional(),
    userId: z.uuid().optional(),
})
    .strict()
    .refine((value) => (value.userId !== undefined &&
    value.externalPhone === undefined &&
    value.externalName === undefined) ||
    (value.userId === undefined &&
        value.externalPhone !== undefined &&
        value.externalName !== undefined), "Provide either a user or an external name and phone, never both.");
const escalationLevelBodyShape = {
    isActive: z.boolean().default(true),
    level: z.number().int().min(1).max(20),
    notifyEmail: z.boolean().default(true),
    notifyWhatsApp: z.boolean().default(true),
    recipients: z.array(escalationRecipientInputSchema).min(1).max(20),
    repeatEveryBusinessDays: z.number().int().min(1).max(60).nullable().default(null),
    thresholdBusinessDays: z.number().int().min(0).max(365),
    title: z.string().trim().min(1).max(120),
    triggerKind: EscalationTriggerKindSchema,
};
/**
 * Repetition only makes sense once the due date is behind us; a preventive step
 * fires once when the remaining days cross its threshold.
 */
function RepeatOnlyWhenOverdue(value) {
    return value.repeatEveryBusinessDays === null || value.triggerKind === "OVERDUE";
}
export const CreateEscalationLevelRequestSchema = z
    .object(escalationLevelBodyShape)
    .strict()
    .refine(RepeatOnlyWhenOverdue, "Only an overdue level may repeat.");
export const UpdateEscalationLevelRequestSchema = z
    .object(escalationLevelBodyShape)
    .strict()
    .refine(RepeatOnlyWhenOverdue, "Only an overdue level may repeat.");
export const EscalationLevelParametersSchema = z
    .object({
    levelId: z.uuid(),
})
    .strict();
//# sourceMappingURL=EscalationContract.js.map